/*
Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
  distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
  prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this
  content, please contact the author for proper authorization and agreement.
  */

import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { UserCircleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline'
import { useAppDispatch } from 'helpers/store'
import SessionAction from 'store/session/SessionAction'
import { useCurrentUser } from 'utils/hooks/useCurrentUser'
import UiUserAvatar from 'components/Ui/UiUserAvatar'

const UiUserMenu: React.FC = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false)
    const [isLoggingOut, setIsLoggingOut] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const { user, fullName } = useCurrentUser()
    const userName = !user ? t('User') : fullName

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleLogout = async () => {
        if (isLoggingOut) {
            return
        }

        setIsLoggingOut(true)
        try {
            await dispatch(SessionAction.logout() as any)
        } finally {
            setIsOpen(false)
            setIsLoggingOut(false)
            navigate('/', { replace: true })
        }
    }

    return (
        <div className="UiUserMenu relative" ref={containerRef}>
            <div
                id="btn-headbar-open-panel"
                onClick={() => setIsOpen(!isOpen)}
                className={clsx(
                    'flex items-center gap-2 cursor-pointer rounded-full p-1 pl-3 transition-colors',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    isOpen && 'bg-gray-100 dark:bg-gray-800'
                )}
            >
                <span className={clsx('font-medium')}>{userName}</span>
                <UiUserAvatar size="sm" alt={userName} />
            </div>

            {isOpen && (
                <div
                    className={clsx(
                        'absolute right-0 top-full mt-2 w-48 rounded-xl bg-white dark:bg-gray-900 shadow-lg py-1 border border-gray-100 dark:border-gray-800 z-1100',
                        'animate-in fade-in zoom-in-95 duration-100 ease-out axis-y'
                    )}
                >
                    <Link
                        to="/profile"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        onClick={() => setIsOpen(false)}
                    >
                        <UserCircleIcon className="size-4" />
                        {t('Profile')}
                    </Link>
                    <div className="h-px bg-gray-100 dark:bg-gray-800 my-1" />
                    <button
                        id="btn-auth-logout-menu"
                        data-testid="btn-auth-logout"
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        aria-busy={isLoggingOut}
                        className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    >
                        {isLoggingOut ? (
                            <span
                                className="size-4 rounded-full border-2 border-current border-r-transparent animate-spin"
                                aria-hidden="true"
                            />
                        ) : (
                            <ArrowRightOnRectangleIcon className="size-4" />
                        )}
                        {isLoggingOut ? t('Logging out...') : t('Log out')}
                    </button>
                </div>
            )}
        </div>
    )
}

export default UiUserMenu
